import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import MoreIcon from '@material-ui/icons/MoreVert';
import cx from 'classnames';
import moment from 'moment-timezone';
import React, { Fragment, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ICollection } from '../../services/types';
import { CopyToClipboard } from '../CopyToClipboard';
import SearchTextField from '../SearchTextField';
import { convertFirstCharacterToUppercase } from '../../services/utils';

const useStyles = makeStyles((theme: Theme) => ({
  collectionListWrap: {
    overflow: 'auto',
  },
  collectionSearchWrap: {
    paddingTop: 15,
    paddingLeft: 15,
    maxWidth: 335,
  },
  collectionRow: {
    height: 55,
    cursor: 'pointer',
  },
  collectionRowNotify: {
    animation: '$fadeout 7s ease-in-out',
    animationDelay: '0.2s',
    animationIterationCount: 1,
  },
  '@keyframes fadeout': {
    ['0%']: {
      backgroundColor: '#fffef1ff',
    },
    ['70%']: {
      backgroundColor: '#fffef1ff',
    },
    ['100%']: {
      backgroundColor: '#fffef100',
    },
  },
  internalTitleEmphasize: {
    fontWeight: 500,
    color: theme.palette.primary.main,
  },
}));

interface IProps {
  collections: ICollection[] | undefined;
  totalCount: number | undefined;

  height?: number;
  compact?: boolean;
  onRequestFetch: (page: number, rowsPerPage: number) => void;
  onRequestSearch?: (keyword: string) => void;
  onDuplicateCollection?: (collection: ICollection) => void;
  onSelectCollection: (collection: ICollection) => void;
  onClickDeleteButton?: (collection: ICollection) => void;
  getLinkUri?: (trackingId: string | undefined) => string;
}

export const CollectionList = (props: IProps): JSX.Element => {
  const {
    getLinkUri,
    collections,
    totalCount,
    height,
    compact,
    onRequestFetch,
    onRequestSearch,
    onDuplicateCollection,
    onSelectCollection,
    onClickDeleteButton,
  } = props;

  const classes = useStyles();

  const [isSearching, setIsSearching] = useState<boolean>();
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [page, setPage] = useState<number>(0);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuFocusedCollectionRef = useRef<ICollection>();

  const handleMoreMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setAnchorEl(null);
  };

  const searchCollections = async (title: string) => {
    if (onRequestSearch) {
      onRequestSearch(title);
      setIsSearching(false);
    }
  };

  const reset = () => {
    setPage(0);
    onRequestFetch(0, rowsPerPage);
  };

  return (
    <Box>
      {onRequestSearch && (
        <Box className={classes.collectionSearchWrap}>
          <SearchTextField
            placeholder="Enter the collection title"
            fullWidth
            onValueChange={(value: string) => {
              if (!value) {
                reset();
              }
            }}
            onCancel={reset}
            onValueSubmit={(value: string) => {
              if (value === '' || value.replace(/\s/g, '') === '') {
                alert('keyword must be longer than or equal to 1 characters.');
              } else {
                setIsSearching(true);
                searchCollections(value);
              }
            }}
            isSearching={isSearching}
          />
        </Box>
      )}

      <Box className={classes.collectionListWrap} style={height ? { height } : undefined}>
        {!collections && (
          <Box padding={2} textAlign="center">
            <CircularProgress size={30} />
          </Box>
        )}
        {collections && (
          <Fragment>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">ID</TableCell>
                  {!compact && (
                    <TableCell align="center" size="small" padding="none">
                      Generate Type
                    </TableCell>
                  )}
                  <TableCell align="center">Collection Title</TableCell>
                  <TableCell align="center">Onboarding?</TableCell>
                  <TableCell align="center">Tracking ID</TableCell>
                  <TableCell align="center">Countries</TableCell>
                  <TableCell align="center">Content Type</TableCell>
                  <TableCell align="center">Created At</TableCell>

                  {!compact && (
                    <TableCell
                      align="center"
                      style={{
                        textAlign: 'left',
                        paddingLeft: 22,
                        width: 110,
                      }}
                    >
                      Delete
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {collections.map(col => {
                  return (
                    <TableRow
                      component={Link}
                      to={getLinkUri ? getLinkUri(col.trackingId) : ''}
                      hover
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        if (!col.trackingId) {
                          return;
                        }

                        if (!getLinkUri) {
                          e.preventDefault();
                        }

                        if (col) {
                          onSelectCollection(col);
                        }
                      }}
                      key={col.id}
                      className={cx({
                        [classes.collectionRow]: true,
                        [classes.collectionRowNotify]: col.notify,
                      })}
                    >
                      <TableCell align="center">{col.id}</TableCell>
                      {!compact && (
                        <TableCell align="center" size="small" padding="none">
                          {col.autoGenerated ? 'Auto' : 'Manual'}
                        </TableCell>
                      )}
                      <TableCell>
                        {col.internalTitle && (
                          <Box display="flex" flexDirection="column">
                            <span className={classes.internalTitleEmphasize}>{col.internalTitle}</span>
                            <span>{col.title}</span>
                          </Box>
                        )}
                        {!col.internalTitle && col.title}
                      </TableCell>
                      <TableCell align="center">{col.isOnboarding ? 'Y' : 'N'}</TableCell>
                      <TableCell align="center">{Boolean(col.trackingId) && <CopyToClipboard content={col.trackingId as string} />}</TableCell>
                      <TableCell align="center">{col.availableCountryCountText}</TableCell>
                      <TableCell align="center">
                        {col.storyTypes?.map(storyType => convertFirstCharacterToUppercase(storyType)).join(', ') || '-'}
                      </TableCell>
                      <TableCell align="center">{moment(col.createdAt).format('lll')}</TableCell>
                      {!compact && (
                        <TableCell
                          align="center"
                          style={{
                            paddingRight: '2px',
                          }}
                        >
                          <Box display="flex">
                            <Tooltip
                              title={'The collection linked to a homeset cannot be deleted.'}
                              open={col.linkCount === 0 ? false : undefined}
                              arrow
                            >
                              <div>
                                <IconButton
                                  style={{
                                    opacity: 1,
                                  }}
                                  color={'primary'}
                                  disabled={col.linkCount > 0}
                                  disableFocusRipple={false}
                                  disableTouchRipple={false}
                                  disableRipple={false}
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClickDeleteButton && onClickDeleteButton(col);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>
                            </Tooltip>

                            <IconButton
                              style={{ padding: '12px 14px' }}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                menuFocusedCollectionRef.current = col;
                                handleMoreMenuClick(e);
                              }}
                            >
                              <MoreIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {totalCount !== undefined && (
              <TablePagination
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={(e: unknown, v: number) => {
                  onRequestFetch(v, rowsPerPage);
                  setPage(v);
                }}
                onChangeRowsPerPage={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const rpp = parseInt(event.target.value, 10);
                  setRowsPerPage(rpp);
                  onRequestFetch(0, rpp);
                  setPage(0);
                }}
              />
            )}
          </Fragment>
        )}
        <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleMoreMenuClose}>
          <MenuItem
            onClick={() => {
              if (onDuplicateCollection && menuFocusedCollectionRef.current) {
                onDuplicateCollection(menuFocusedCollectionRef.current);
                setAnchorEl(null);
              }
            }}
          >
            Duplicate
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};
